'use client'

import { KitAvatar } from '@repo/features/avatar'
import { z } from '@repo/schema'
import { GetStatusSchema } from '@repo/schema/status'
import { GetUserSchema } from '@repo/schema/user'
import {
  Button,
  buttonVariants,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Field,
  FieldContent,
  FieldError,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  useCommandState,
} from '@repo/ui/components'
import { Check } from '@repo/ui/icons'
import { VariantProps } from '@repo/ui/lib/utils'
import { cn } from '@repo/ui/lib/utils'
import { ComponentType } from 'react'
import { ComponentProps, Suspense, useState } from 'react'
import { useFieldContext } from '../../lib/form-context.ts'

const userSchema = GetUserSchema.extend({
  status: GetStatusSchema,
})

export type User = z.infer<typeof userSchema>

type FormUserSelectProps = ComponentProps<'button'> & {
  className?: string
  disabled?: boolean
  isPending?: boolean
  label?: string
  UserFetcher: ComponentType<{
    onSelect: (user: null | User) => void
    selectedUserId?: number
  }>
  variant?: VariantProps<typeof buttonVariants>['variant']
}

export const FormUserSelect = ({
  className,
  disabled,
  isPending,
  label = 'Select user...',
  UserFetcher,
  variant = 'outline',
  ...props
}: FormUserSelectProps) => {
  const [open, setOpen] = useState(false)
  const field = useFieldContext<null | undefined | User>()

  const handleSelect = async (user: null | User) => {
    field.handleChange(user)
    field.handleBlur()
    setOpen(false)
  }

  return (
    <Field className="gap-1" data-sentry-element="Field" data-sentry-component="FormUserSelect" data-sentry-source-file="form-user-select.tsx">
      <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-user-select.tsx">
        <Popover onOpenChange={setOpen} open={open} data-sentry-element="Popover" data-sentry-source-file="form-user-select.tsx">
          <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="form-user-select.tsx">
            <Button
              align="left"
              className={cn('flex w-full items-center text-xs', className)}
              disabled={disabled || isPending}
              size={1}
              type="button"
              variant={variant}
              {...props}
            data-sentry-element="Button" data-sentry-source-file="form-user-select.tsx">
              <UserDisplay label={label} user={field.state.value} data-sentry-element="UserDisplay" data-sentry-source-file="form-user-select.tsx" />
              {isPending && <Spinner />}
            </Button>
          </PopoverTrigger>

          <PopoverContent align="end" className="p-0" data-sentry-element="PopoverContent" data-sentry-source-file="form-user-select.tsx">
            <Command shouldFilter={false} data-sentry-element="Command" data-sentry-source-file="form-user-select.tsx">
              <CommandInput placeholder="Search user" data-sentry-element="CommandInput" data-sentry-source-file="form-user-select.tsx" />
              <Suspense fallback={<UserSelectSkeleton />} data-sentry-element="Suspense" data-sentry-source-file="form-user-select.tsx">
                <UserFetcher onSelect={handleSelect} selectedUserId={field.state.value?.id} data-sentry-element="UserFetcher" data-sentry-source-file="form-user-select.tsx" />
              </Suspense>
            </Command>
          </PopoverContent>
        </Popover>
      </FieldContent>
      <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="form-user-select.tsx" />
    </Field>
  )
}

const UserDisplay = ({ label, user }: { label: string; user?: null | User }) => {
  return (
    <>
      <KitAvatar
        className={cn('size-5', !user && 'stroke-muted-foreground')}
        textClassName="text-xs"
        user={user ?? undefined}
      data-sentry-element="KitAvatar" data-sentry-source-file="form-user-select.tsx" />
      {!user ? (
        <p className="font-semibold">{label}</p>
      ) : (
        <div className="font-semibold">
          {user.name} {user.last_name}
        </div>
      )}
    </>
  )
}

const UserSelectSkeleton = () => {
  return (
    <div className="space-y-2 p-2" data-sentry-component="UserSelectSkeleton" data-sentry-source-file="form-user-select.tsx">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          className="h-12 w-full animate-pulse rounded bg-neutral-100 dark:bg-neutral-800"
          key={i}
        />
      ))}
    </div>
  )
}

export const UserList = ({
  onSelect,
  selectedUserId,
  users,
}: {
  onSelect: (user: null | User) => void
  selectedUserId?: number
  users: User[]
}) => {
  return (
    <CommandList data-sentry-element="CommandList" data-sentry-component="UserList" data-sentry-source-file="form-user-select.tsx">
      <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="form-user-select.tsx">No results found</CommandEmpty>
      <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="form-user-select.tsx">
        {selectedUserId && (
          <>
            <CommandItem onSelect={() => onSelect(null)} value="unassign">
              <div className="flex items-center gap-2">
                <KitAvatar className="size-8" textClassName="text-xs" />
                <p>Unassign</p>
              </div>
            </CommandItem>
            <CommandSeparator />
          </>
        )}
        {users.map(user => (
          <CommandItem
            className="flex items-center justify-between"
            key={user.id}
            onSelect={() => onSelect(user)}
            value={`${user.name} ${user.last_name} ${user.id}`}
          >
            <div className="flex items-center gap-2">
              <KitAvatar className="size-8" textClassName="text-xs" user={user} />
              <div className="flex flex-col gap-2">
                <p>
                  {user.name} {user.last_name}
                </p>
              </div>
            </div>
            {selectedUserId && user?.id === selectedUserId && <Check />}
          </CommandItem>
        ))}
      </CommandGroup>
    </CommandList>
  )
}

export const createUserFetcher = (
  useFetchHook: (params: { perPage?: number; search?: string }) => User[],
) => {
  return function UserFetcher({
    onSelect,
    selectedUserId,
  }: {
    onSelect: (user: null | User) => void
    selectedUserId?: number
  }) {
    const search = useCommandState(state => state.search)
    const users = useFetchHook({ search })
    return <UserList onSelect={onSelect} selectedUserId={selectedUserId} users={users} />
  }
}
